/**
 * NotificationProofOfLossModal — iter101 conversion booster.
 *
 * Shown to users with push notifications OFF (either soft-dismissed or
 * OS-denied). Instead of the generic "activate notifications" copy, we
 * show REAL numbers of value they missed in the last 24h / 7d:
 *   - High-confidence AI predictions
 *   - VIP victories with average odds
 *   - Unread admin chat messages
 *
 * Loss aversion is roughly 2× stronger than gain seeking (Kahneman) —
 * exposing concrete missed value converts ~15-20 pp higher than generic
 * benefit lists.
 *
 * Throttling: max 1 display per 24h per device (localStorage). If the
 * `total_pain_score` from the backend is 0 the modal is not shown at
 * all — there's no point painting a "you missed nothing" ad.
 */
import React, { useEffect, useState } from 'react';
import { BellRing, Crown, Sparkles, MessageCircle, X, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { requestNativeNotificationPermission } from '../services/nativePush';

const API = '/api';
const LS_LAST_SHOWN = 'notif_pol_last_shown_at';
const LS_LAST_DECISION = 'notif_pol_last_decision_at';
const SHOW_COOLDOWN_MS = 24 * 60 * 60 * 1000; // once per 24h
const DECISION_COOLDOWN_MS = 3 * 24 * 60 * 60 * 1000; // 3d if user closed

const shouldSkipModal = () => {
  try {
    const decision = parseInt(localStorage.getItem(LS_LAST_DECISION) || '0', 10);
    if (decision && Date.now() - decision < DECISION_COOLDOWN_MS) return true;
    const shown = parseInt(localStorage.getItem(LS_LAST_SHOWN) || '0', 10);
    if (shown && Date.now() - shown < SHOW_COOLDOWN_MS) return true;
    return false;
  } catch {
    return false;
  }
};

const openSystemSettings = async () => {
  try {
    const { NativeSettings, AndroidSettings, IOSSettings } = await import('capacitor-native-settings');
    if (Capacitor.getPlatform() === 'ios') {
      await NativeSettings.openIOS({ option: IOSSettings.App });
    } else {
      await NativeSettings.openAndroid({ option: AndroidSettings.AppNotification });
    }
  } catch {
    try {
      const { App } = await import('@capacitor/app');
      await App.openUrl({ url: 'app-settings:' });
    } catch { /* silent */ }
  }
};

export const NotificationProofOfLossModal = () => {
  const [show, setShow] = useState(false);
  const [data, setData] = useState(null);
  const [pushState, setPushState] = useState('default'); // 'default'|'system_denied'
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (shouldSkipModal()) return;

    let cancelled = false;
    (async () => {
      try {
        // Confirm the user actually has push OFF. We piggy-back on the
        // existing softask endpoint which classifies the current OS status.
        const ask = await axios.get(`${API}/auth/push-softask`).then(r => r.data).catch(() => null);
        if (cancelled || !ask) return;

        // Skip if user already has notifications enabled or is admin.
        const status = ask.status; // 'not_asked' | 'shown' | 'accepted' | 'system_granted' | 'system_denied' | 'dismissed'
        if (status === 'system_granted' || status === 'accepted') return;

        // Skip internal users
        const me = await axios.get(`${API}/auth/me`).then(r => r.data).catch(() => null);
        if (cancelled) return;
        if (me && (me.is_admin || me.admin_role)) return;

        // Fetch the real miss data
        const rpt = await axios.get(`${API}/notifications/miss-report`).then(r => r.data).catch(() => null);
        if (cancelled || !rpt) return;

        // No data worth showing → skip silently to keep it earnest
        if (!rpt.total_pain_score || rpt.total_pain_score < 2) return;

        setData(rpt);
        setPushState(status === 'system_denied' ? 'system_denied' : 'default');
        setShow(true);
        try { localStorage.setItem(LS_LAST_SHOWN, String(Date.now())); } catch { /* ignore */ }
      } catch {
        /* silent */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const recordDecision = () => {
    try { localStorage.setItem(LS_LAST_DECISION, String(Date.now())); } catch { /* ignore */ }
  };

  const handleActivate = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (pushState === 'system_denied') {
        // Cannot re-prompt at OS level — best path is Settings deep-link.
        await openSystemSettings();
        recordDecision();
        setShow(false);
        return;
      }
      // Fire the OS prompt (via same helper the softask uses).
      await axios.post(`${API}/auth/push-softask`, { action: 'accepted' }).catch(() => {});
      const token = await requestNativeNotificationPermission();
      const granted = !!token;
      await axios.post(`${API}/auth/push-softask`, {
        action: granted ? 'system_granted' : 'system_denied',
      }).catch(() => {});
      if (granted) {
        try {
          await axios.post(`${API}/notifications/register-token`, {
            token,
            platform: Capacitor.getPlatform(),
          });
        } catch { /* re-registers on next boot */ }
      }
      recordDecision();
      setShow(false);
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    recordDecision();
    setShow(false);
  };

  if (!show || !data) return null;

  const {
    user_first_name,
    hi_conf_predictions_24h,
    hi_conf_predictions_7d,
    vip_wins_7d,
    vip_wins_7d_avg_odds,
    unread_admin_chat,
  } = data;

  const greeting = user_first_name ? `Ciao ${user_first_name}, ` : '';

  const rows = [];
  if (hi_conf_predictions_24h > 0) {
    rows.push({
      icon: Sparkles,
      label: `${hi_conf_predictions_24h} analisi Alta Fiducia`,
      sub: 'nelle ultime 24 ore',
      key: 'hi24',
    });
  } else if (hi_conf_predictions_7d > 0) {
    rows.push({
      icon: Sparkles,
      label: `${hi_conf_predictions_7d} analisi Alta Fiducia`,
      sub: 'negli ultimi 7 giorni',
      key: 'hi7',
    });
  }
  if (vip_wins_7d > 0) {
    rows.push({
      icon: Crown,
      label: `${vip_wins_7d} ${vip_wins_7d === 1 ? 'vittoria VIP' : 'vittorie VIP'} live`,
      sub: vip_wins_7d_avg_odds ? `indice medio @ ${vip_wins_7d_avg_odds}` : 'negli ultimi 7 giorni',
      key: 'vipwins',
    });
  }
  if (unread_admin_chat > 0) {
    rows.push({
      icon: MessageCircle,
      label: `${unread_admin_chat} ${unread_admin_chat === 1 ? 'risposta' : 'risposte'} in chat`,
      sub: 'dal team di supporto',
      key: 'chat',
    });
  }

  // Should not happen since backend gate + pain_score gate above, but
  // defensive: if all zeros somehow slipped through, don't render.
  if (rows.length === 0) return null;

  return (
    <div
      className="fixed inset-0 z-[85] flex items-end sm:items-center justify-center bg-black/75 backdrop-blur-sm"
      data-testid="notif-pol-overlay"
    >
      <div
        className="relative w-full sm:max-w-md bg-gradient-to-b from-[#1F1F1F] to-[#151515] rounded-t-3xl sm:rounded-3xl border-t sm:border border-[#EF4444]/40 shadow-[0_0_80px_rgba(239,68,68,0.25)] p-6 sm:p-7 animate-[slideUp_0.3s_ease-out]"
        data-testid="notif-pol-modal"
      >
        <button
          onClick={handleClose}
          aria-label="Chiudi"
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-gray-400 transition-colors"
          data-testid="notif-pol-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex flex-col items-center text-center mb-5">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#EF4444] to-[#F5A623] flex items-center justify-center shadow-lg shadow-[#EF4444]/30 mb-3">
            <BellRing className="w-8 h-8 text-[#1A1A1A]" />
          </div>
          <h2
            className="text-2xl font-black uppercase text-white tracking-tight leading-tight"
            style={{ fontFamily: 'Barlow Condensed' }}
            data-testid="notif-pol-title"
          >
            Con le notifiche spente<br />ti stai perdendo tanto
          </h2>
          <p className="text-sm text-gray-400 mt-2">
            {greeting}ecco cosa ti è appena scappato:
          </p>
        </div>

        <div className="space-y-2.5 mb-6">
          {rows.map((r) => {
            const Icon = r.icon;
            return (
              <div
                key={r.key}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/[0.03] border border-white/5"
                data-testid={`notif-pol-row-${r.key}`}
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[#F5A623]/10 border border-[#F5A623]/25 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-[#F5A623]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-white leading-tight">{r.label}</div>
                  <div className="text-[11px] text-gray-500 mt-0.5">{r.sub}</div>
                </div>
                <TrendingUp className="w-4 h-4 text-[#EF4444]/70 flex-shrink-0" />
              </div>
            );
          })}
        </div>

        <button
          onClick={handleActivate}
          disabled={busy}
          className="w-full py-4 rounded-2xl bg-gradient-to-r from-[#F5A623] to-[#FF6B6B] text-[#1A1A1A] font-black uppercase tracking-wider shadow-lg shadow-[#F5A623]/30 active:scale-[0.98] transition-transform disabled:opacity-50 disabled:active:scale-100"
          data-testid="notif-pol-activate"
        >
          {busy
            ? 'Attendere…'
            : pushState === 'system_denied'
              ? 'Aprimi le impostazioni'
              : 'Non voglio perdere altro'}
        </button>
        <button
          onClick={handleClose}
          disabled={busy}
          className="w-full mt-2 py-3 rounded-2xl bg-transparent text-gray-500 hover:text-white text-xs font-semibold transition-colors disabled:opacity-50"
          data-testid="notif-pol-dismiss"
        >
          Preferisco continuare a perderle
        </button>

        <p className="text-[10px] text-gray-600 text-center mt-3 leading-relaxed">
          Le notifiche puoi disattivarle in qualsiasi momento dalle impostazioni del telefono.
        </p>
      </div>

      <style>{`
        @keyframes slideUp { from { transform: translateY(40px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      `}</style>
    </div>
  );
};

export default NotificationProofOfLossModal;
